import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

const PREFILL_MESSAGE_TYPE = 'Prefill';
const REQUEST_PREFILL_MESSAGE_TYPE = 'RequestPrefill';
const MESSAGE_EVENT = 'message';

interface PrefillContextValue {
  prefillValues: Record<string, string>;
}

const PrefillContext = createContext<PrefillContextValue>({ prefillValues: {} });

function parsePrefillMap(prefillMap: string): Record<string, string> {
  if (!prefillMap) return {};

  const mapping: Record<string, string> = {};

  for (const entry of prefillMap.split(',')) {
    const separatorIndex = entry.indexOf(':');
    if (separatorIndex === -1) continue;

    const controlName = entry.substring(0, separatorIndex).trim();
    const sourcePath = entry.substring(separatorIndex + 1).trim();

    if (controlName && sourcePath) {
      mapping[controlName] = sourcePath;
    }
  }

  return mapping;
}

interface PrefillProviderProps {
  prefillMap: string;
  children: ReactNode;
}

export const PrefillProvider: React.FC<PrefillProviderProps> = ({ prefillMap, children }) => {
  const [prefillValues, setPrefillValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const mapping = parsePrefillMap(prefillMap);
    if (Object.keys(mapping).length === 0) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === PREFILL_MESSAGE_TYPE && event.data.values) {
        setPrefillValues(event.data.values);
      }
    };

    window.addEventListener(MESSAGE_EVENT, handleMessage);

    window.parent?.postMessage({ type: REQUEST_PREFILL_MESSAGE_TYPE, mapping }, '*');

    return () => {
      window.removeEventListener(MESSAGE_EVENT, handleMessage);
    };
  }, [prefillMap]);

  return <PrefillContext.Provider value={{ prefillValues }}>{children}</PrefillContext.Provider>;
};

export const usePrefill = (controlName: string): string | undefined => {
  const { prefillValues } = useContext(PrefillContext);
  return prefillValues[controlName];
};
